// ingestionStatusDisplay.js
// How a file's or a unit's status is shown: its words, its colour and its icon,
// and the one-line summary of a file's ledger. Shared by the file card, the file
// review dialog, the unit cards and the files table, so every surface uses the
// same words.
//
// The statuses themselves are derived in utils/ingestionStatus.js; nothing here
// decides a status, it only presents one.

import React from 'react';
import { CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import HelpIcon from '@mui/icons-material/Help';
import InfoIcon from '@mui/icons-material/Info';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { bucketOf, FILE_LABEL, UNIT_LABEL } from '../../../utils/ingestionStatus';
import { countNoun } from '../../../utils/pluralise';
import { formatTimestamp } from '../../../utils/timestamps';

const ICON_SIZE = 18;

// ── Files ────────────────────────────────────────────────────────────────────

// What a table's sidebar entry and the files of its batch say while the table's
// claim reads `blocked`: a landing committed its rows but could not be finished,
// and only support can finish it (Spec 4 section 6). The backend's
// LANDING_ATTENTION_MESSAGE in functions/ingestion/load/landing.py.
export const LANDING_ATTENTION_MESSAGE = 'Landing needs attention — contact support';

export function fileStatusLabel(status) {
  return FILE_LABEL[status] || status;
}

// The MUI colour of a file's status chip.
export function fileStatusColour(status) {
  if (status === 'completed') return 'success';
  if (status === 'Failed' || status === 'DeleteFailed') return 'error';
  // A parked file is waiting on answers to the check's questions.
  if (status === 'parked') return 'secondary';
  if (bucketOf(status) === 'attention') return 'warning';
  return 'info';
}

const FILE_STATUSES_IN_MOTION = new Set(['Cleaning', 'Ready', 'running', 'waiting', 'AwaitingDeletion', 'Deleting']);

export function FileStatusIcon({ status }) {
  if (FILE_STATUSES_IN_MOTION.has(status)) return <CircularProgress size={ICON_SIZE - 4} />;
  if (status === 'Failed' || status === 'DeleteFailed') return <ErrorIcon color="error" sx={{ fontSize: ICON_SIZE }} />;
  if (status === 'Reviewing' || status === 'AwaitingApproval') return <WarningIcon color="warning" sx={{ fontSize: ICON_SIZE }} />;
  if (status === 'parked') return <HelpIcon color="secondary" sx={{ fontSize: ICON_SIZE }} />;
  if (status === 'completed') return <CheckCircleIcon color="success" sx={{ fontSize: ICON_SIZE }} />;
  return <InfoIcon color="info" sx={{ fontSize: ICON_SIZE }} />;
}

// ── Units ────────────────────────────────────────────────────────────────────

// The word a unit's chip shows. 'Ignored' covers both a unit used as header
// values for the other sheets and one left out altogether, so the disposition
// tells them apart.
export function unitStatusLabel(status, disposition) {
  if (status === 'Ignored') return disposition === 'context' ? 'Header values' : 'Ignored';
  return UNIT_LABEL[status] || status;
}

export function unitStatusColour(status) {
  if (status === 'Ready' || status === 'Completed') return 'success';
  if (status === 'Failed') return 'error';
  if (status === 'Cleaning' || status === 'AwaitingValidation') return 'info';
  if (status === 'Ignored') return 'default';
  return 'warning';
}

export function UnitStatusIcon({ status, disposition }) {
  if (status === 'Ignored') {
    return disposition === 'context'
      ? <InfoIcon color="info" sx={{ fontSize: ICON_SIZE }} />
      : <RemoveCircleIcon color="disabled" sx={{ fontSize: ICON_SIZE }} />;
  }
  if (status === 'Cleaning' || status === 'AwaitingValidation') return <CircularProgress size={ICON_SIZE - 4} />;
  if (status === 'Ready' || status === 'Completed') return <CheckCircleIcon color="success" sx={{ fontSize: ICON_SIZE }} />;
  if (status === 'Failed') return <ErrorIcon color="error" sx={{ fontSize: ICON_SIZE }} />;
  return <WarningIcon color="warning" sx={{ fontSize: ICON_SIZE }} />;
}

// ── The ledger summary ───────────────────────────────────────────────────────

// The order the counts are listed in, what needs the person first. A ledger is a
// map, so without a fixed order the line could reshuffle from one snapshot to
// the next.
const LEDGER_GROUP_ORDER = [
  'Failed', 'MissingRowIdentifier', 'ZeroColumnsMapped', 'PreviewReady', 'Cleaning',
  'Ready', 'AwaitingValidation', 'Completed', 'context', 'ignore',
];

// One line about a file's tables, read from its ledger alone, as
// "3 tables: 2 to review, 1 header values · 1 warning".
export function ledgerSummaryLine(file) {
  const entries = Object.values(file.units || {});
  if (entries.length === 0) {
    return file.status === 'Cleaning' ? 'Reading the file…' : 'Nothing to review';
  }
  // An ingest unit is counted under its status; the others under what they are.
  const counts = new Map();
  entries.forEach((entry) => {
    const isIngest = entry.disposition === 'ingest';
    const group = isIngest ? entry.status : entry.disposition;
    if (!counts.has(group)) {
      const label = isIngest
        ? unitStatusLabel(entry.status, entry.disposition)
        : unitStatusLabel('Ignored', entry.disposition);
      counts.set(group, { label: label.toLowerCase(), count: 0 });
    }
    counts.get(group).count += 1;
  });
  const rank = (group) => {
    const position = LEDGER_GROUP_ORDER.indexOf(group);
    return position === -1 ? LEDGER_GROUP_ORDER.length : position;
  };
  const parts = Array.from(counts.entries())
    .sort(([left], [right]) => rank(left) - rank(right))
    .map(([, { label, count }]) => `${count} ${label}`);
  const warnings = entries.reduce((total, entry) => total + entry.warnings, 0);
  const warningText = warnings > 0 ? ` · ${countNoun(warnings, 'warning')}` : '';
  return `${countNoun(entries.length, 'table')}: ${parts.join(', ')}${warningText}`;
}

// "Approved by you, 01 Nov 26, 14:32". Approvals record the approver's user id,
// and the platform keeps no directory of other users' names, so anyone but the
// person looking is "another user".
export function approvalLine(approved, currentUid) {
  const who = approved.by === currentUid ? 'you' : 'another user';
  return `Approved by ${who}, ${formatTimestamp(approved.at)}`;
}
